import * as React from "react";
import { Alert, Button, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import { gettext } from "i18n";
import { downloadBackupCodes } from "utils/mfa";

interface BackupCodesDisplayProps {
  codes: string[];
  // Called once the user confirms they have saved the codes
  onDone: () => void;
}

/**
 * New backup codes, shown only this once: the user has to confirm that they
 * have saved them before going on.
 */
const BackupCodesDisplay: React.FC<BackupCodesDisplayProps> = ({
  codes,
  onDone,
}) => {
  const [saved, setSaved] = React.useState<boolean>(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      toast.success(gettext("Backup codes copied"));
    } catch (e) {
      toast.error(gettext("Could not copy the codes, please write them down"));
    }
  };

  return (
    <div className="BackupCodesDisplay">
      <Alert variant="warning">
        {gettext(
          "Save these backup codes somewhere safe, e.g. in a password manager. If you lose your phone or passkey, you can log in with one of them. Each code works once. They will not be shown again.",
        )}
      </Alert>
      {/* Two columns even on the narrowest phones: a code never wraps */}
      <ul className="list-unstyled font-monospace fs-0 text-body bg-light rounded row gx-2 gy-1 mx-0 px-1 py-2 mb-3">
        {codes.map((code) => (
          <li key={code} className="col-6 text-nowrap text-center">
            {code}
          </li>
        ))}
      </ul>
      <div className="d-flex gap-2 mb-3">
        <Button variant="outline-secondary" size="sm" onClick={copy}>
          {gettext("Copy")}
        </Button>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => downloadBackupCodes(codes)}
        >
          {gettext("Download")}
        </Button>
      </div>
      <Form.Check
        id="backup-codes-saved"
        type="checkbox"
        label={gettext("I have saved my backup codes")}
        checked={saved}
        onChange={(e) => setSaved(e.target.checked)}
        className="mb-3"
      />
      <div className="d-grid d-sm-block">
        <Button disabled={!saved} onClick={onDone}>
          {gettext("Continue")}
        </Button>
      </div>
    </div>
  );
};

export default BackupCodesDisplay;
